/**
 * Ability sigils for the HUD — drawn inline so they inherit `currentColor` (the
 * slot's `--accent`) and need no image assets.
 *
 * A 100×100 box, stroke only, so the mark reads the same at 34px in the ability
 * slot as it does scaled up.
 */

const WRAP = (body) =>
  `<svg class="glyph-svg" viewBox="0 0 100 100" aria-hidden="true" fill="none"
     stroke="currentColor" stroke-width="4.2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;

/**
 * Ice — a six-fold snowflake over a rising lance.
 *
 * Three axes at 60°, each with a pair of barbs, and a heavier vertical that runs
 * past the star into a point: the star says frost, the point says skillshot.
 */
const ICE = WRAP(`
  <path d="M50 12V88"/>
  <path d="M17.5 30.5L82.5 69.5"/>
  <path d="M82.5 30.5L17.5 69.5"/>
  <path d="M50 24L41 33M50 24L59 33"/>
  <path d="M50 76L41 67M50 76L59 67"/>
  <path d="M27.5 36.5L27.7 49.2M27.5 36.5L38.5 30.4"/>
  <path d="M72.5 63.5L72.3 50.8M72.5 63.5L61.5 69.6"/>
  <path d="M72.5 36.5L72.3 49.2M72.5 36.5L61.5 30.4"/>
  <path d="M27.5 63.5L27.7 50.8M27.5 63.5L38.5 69.6"/>
`);

/**
 * Thunder — a bolt struck through a pair of arcs.
 *
 * The zigzag is drawn on the same diagonal the cast travels on, and the two
 * open arcs behind it read as the discharge spreading off it. Stroke only, like
 * the snowflake, so the two slots sit at the same visual weight.
 */
const THUNDER = WRAP(`
  <path d="M60 10L30 52H49L40 90L72 45H52L60 10Z"/>
  <path d="M23 26C13 36 11 52 17 65"/>
  <path d="M84 34C90 47 88 63 78 73"/>
`);

/**
 * Meteor — a cracked ball trailing fire.
 *
 * The circle sits forward and low with three seams splitting it, and three
 * tapering streaks run back up the same diagonal the other two sigils are drawn
 * on, so the slot reads as "the rock, thrown" at 34px.
 */
const METEOR = WRAP(`
  <circle cx="62" cy="62" r="24"/>
  <path d="M46 45L58 58L52 72M74 46L66 60L79 72M58 84L64 70"/>
  <path d="M30 70L10 90M40 34L18 22M22 48L4 44"/>
`);

/**
 * Beam — a charge held in a bracket, firing a cone.
 *
 * The orb sits low-left where the other three sigils start their diagonal, two
 * open brackets behind it read as the hands holding it, and three tapering rays
 * open out to the upper right with a single wave threaded through them: the
 * column, and the coil wrapped around it.
 */
const BEAM = WRAP(`
  <circle cx="27" cy="66" r="11"/>
  <path d="M13 55C7 62 7 74 13 81"/>
  <path d="M40 79C47 73 47 61 40 55"/>
  <path d="M41 57L92 20M42 66L94 50M43 75L92 80"/>
  <path d="M46 63C56 49 64 71 74 57C82 46 88 52 93 46"/>
`);

/**
 * Snare — a ring with a bolt standing in it.
 *
 * The only sigil in the set built around a *circle you look into* rather than a
 * diagonal, because that is the one thing this slot has to say before anything
 * else: it is not a skillshot, it is a footprint. The ellipse is the boundary
 * seen in perspective, four arcs step around it where the rim current runs, and
 * the zigzag rises out of the middle.
 */
const SNARE = WRAP(`
  <ellipse cx="50" cy="70" rx="38" ry="15"/>
  <path d="M12 70L4 70M88 70L96 70M31 82L27 89M69 82L73 89"/>
  <path d="M56 18L38 46H50L44 68"/>
  <path d="M50 55L62 40H52L58 26"/>
`);

/**
 * Glacier — a crown of blades standing on a ring.
 *
 * The second sigil built around a *circle you look into*, because it is the
 * second far cast and that is the first thing the slot has to say. Where the
 * Snare stands one bolt in the middle of its ellipse, this one stands the ring
 * itself up: five blades of uneven height rising off the boundary with the
 * spire tallest in the middle, which is the silhouette the ability actually
 * makes.
 */
const GLACIER = WRAP(`
  <ellipse cx="50" cy="74" rx="38" ry="13"/>
  <path d="M9 70L13 41L21 66"/>
  <path d="M24 65L30 31L37 60"/>
  <path d="M42 61L50 15L58 61"/>
  <path d="M63 60L70 31L76 65"/>
  <path d="M79 66L87 41L91 70"/>
`);

/**
 * Arc Ray — a narrow discharge with two short side arcs.
 */
const RAY = WRAP(`
  <path d="M59 8L34 48H52L42 92L72 43H53L59 8Z"/>
  <path d="M25 35C15 43 14 54 19 63"/>
  <path d="M78 39C87 47 87 58 81 67"/>
`);

/**
 * Backflow Rift — a gate with an arrow that folds back through itself.
 */
const BACKFLOW = WRAP(`
  <ellipse cx="50" cy="70" rx="37" ry="15"/>
  <path d="M16 70C25 47 38 35 52 30C66 25 79 35 85 51"/>
  <path d="M84 51L73 48M84 51L80 61"/>
  <path d="M50 20L44 32L56 32Z"/>
`);

const PRISM = WRAP(`
  <path d="M50 12L89 80H11L50 12Z"/>
  <path d="M50 12V80M11 80L70 47M89 80L30 47"/>
  <path d="M50 38L63 60L50 73L37 60L50 38Z"/>
`);

const HORIZON = WRAP(`
  <ellipse cx="50" cy="53" rx="39" ry="17"/>
  <circle cx="50" cy="53" r="15"/>
  <path d="M17 34C31 15 67 12 82 33C92 47 82 67 63 73"/>
  <path d="M82 72C64 91 31 86 18 67"/>
`);

const RAZOR = WRAP(`
  <path d="M15 23C39 29 42 48 19 58C30 38 26 30 15 23Z"/>
  <path d="M39 31C64 38 65 58 42 68C54 48 50 39 39 31Z"/>
  <path d="M64 40C89 47 89 67 67 78C78 57 75 48 64 40Z"/>
  <path d="M10 78L90 20"/>
`);

const TIDE = WRAP(`
  <path d="M13 72C16 39 30 21 50 21C70 21 84 39 87 72"/>
  <ellipse cx="50" cy="73" rx="38" ry="12"/>
  <path d="M34 44C29 53 32 59 38 59C44 59 46 52 40 44L37 39L34 44Z"/>
  <path d="M60 35C55 44 58 50 64 50C70 50 72 43 66 35L63 30L60 35Z"/>
`);

const SUNWHEEL = WRAP(`
  <circle cx="50" cy="53" r="34"/>
  <circle cx="50" cy="53" r="8"/>
  <path d="M50 53L80 31"/>
  <path d="M50 12V21M50 85V94M9 53H18M82 53H91"/>
  <path d="M21 24L28 31M72 75L79 82M79 24L72 31M28 75L21 82"/>
`);

/** Fault Fold — a broken seam shouldering two slabs out of the floor. */
const FAULTFOLD = WRAP(`
  <path d="M8 76L29 49L47 62L65 33L92 20"/>
  <path d="M8 87L32 61L48 75L69 46L92 34"/>
  <path d="M29 49V70M47 62V75M65 33V57"/>
  <path d="M18 65L11 54M76 39L83 51"/>
`);

/** Bramble Orrery — three thorn sentries orbiting a living core. */
const BRAMBLE = WRAP(`
  <circle cx="50" cy="51" r="10"/>
  <ellipse cx="50" cy="51" rx="40" ry="22" transform="rotate(-18 50 51)"/>
  <path d="M17 59L10 50L21 48L17 59ZM64 25L68 14L76 22L64 25ZM78 69L90 70L84 80L78 69Z"/>
  <path d="M42 45L35 38M58 56L66 63M46 61L40 72"/>
  <path d="M37 40L37 33M64 61L71 60M41 69L34 68"/>
`);

/** Cataclysm Fireball — a dense stellar core wrapped in three magic orbits. */
const CATACLYSM = WRAP(`
  <circle cx="50" cy="50" r="17"/>
  <circle cx="50" cy="50" r="6"/>
  <ellipse cx="50" cy="50" rx="42" ry="20" transform="rotate(-22 50 50)"/>
  <ellipse cx="50" cy="50" rx="42" ry="20" transform="rotate(38 50 50)"/>
  <path d="M50 6L55 18L50 24L45 18Z M50 76L55 84L50 95L45 84Z"/>
  <path d="M13 28L25 31M75 69L88 72M16 76L27 68M73 32L85 23"/>
`);

/** Runic Aegis — concentric geometry and an original eight-point arcane seal. */
const RUNIC = WRAP(`
  <circle cx="50" cy="50" r="42"/>
  <circle cx="50" cy="50" r="31"/>
  <path d="M50 17L73 27L83 50L73 73L50 83L27 73L17 50L27 27Z"/>
  <path d="M50 28L61 39L72 50L61 61L50 72L39 61L28 50L39 39Z"/>
  <circle cx="50" cy="50" r="8"/>
  <path d="M50 8V17M50 83V92M8 50H17M83 50H92M20 20L27 27M73 73L80 80M80 20L73 27M27 73L20 80"/>
`);

/** Passo Sombrio — a split footprint crossing into its waiting echo. */
const SHADOWSTEP = WRAP(`
  <path d="M28 16C42 20 47 34 42 48C38 59 27 67 18 79C35 76 46 82 52 91"/>
  <path d="M72 16C58 20 53 34 58 48C62 59 73 67 82 79C65 76 54 82 48 91"/>
  <path d="M35 35C43 27 57 27 65 35"/>
  <path d="M31 58C41 64 59 64 69 58"/>
  <path d="M17 79L10 65M83 79L90 65"/>
  <circle cx="42" cy="42" r="2.5" fill="currentColor" stroke="none"/>
  <circle cx="58" cy="42" r="2.5" fill="currentColor" stroke="none"/>
`);

/** Véu Predatório — broken eye and split cloak silhouette. */
const CAMOUFLAGE = WRAP(`
  <path d="M12 50C22 30 39 20 50 20C61 20 78 30 88 50C78 43 63 39 50 39C37 39 22 43 12 50Z"/>
  <path d="M12 50C22 70 39 80 50 80C61 80 78 70 88 50C77 57 64 61 50 61C36 61 23 57 12 50Z"/>
  <path d="M50 10L44 32M50 90L56 68M24 25L34 39M76 75L66 61"/>
  <circle cx="50" cy="50" r="5" fill="currentColor" stroke="none"/>
  <path d="M42 50H29M58 50H71"/>
`);

/** Keyed by the ids in `ELEMENTS`. */
export const ELEMENT_SIGILS = {
  ice: ICE,
  thunder: THUNDER,
  meteor: METEOR,
  beam: BEAM,
  snare: SNARE,
  glacier: GLACIER,
  ray: RAY,
  backflow: BACKFLOW,
  prism: PRISM,
  horizon: HORIZON,
  razor: RAZOR,
  tide: TIDE,
  sunwheel: SUNWHEEL,
  faultfold: FAULTFOLD,
  bramble: BRAMBLE,
  cataclysm: CATACLYSM,
  runic: RUNIC,
  shadowstep: SHADOWSTEP,
  camouflage: CAMOUFLAGE
};
